import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingView from "../../components/LoadingView";
import EmptyStateView from "../../components/EmptyStateView";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { clean } from "../../utils/utils";
import ConversationDetailInput from "./components/ConversationDetailInput";
import { useConversation } from "../../contexts/ConversationContext";
import ConversationHeaderView from "./components/ConversationHeaderView";
import MessageListItem from "./components/MessageListItem";
import { useFile } from "../../contexts/FileContext";

const ConversationDetailPage: React.FC = () => {
  const { user } = useAuth();
  const { updateUser } = useUser();
  const { createFile } = useFile();
  const { conversationId } = useParams<{ conversationId: string }>();
  const {
    selectedConversation: conversation,
    messages,
    createMessageForConversation,
    setSelectedConversation,
  } = useConversation();

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const hasSyncedUser = useRef(false);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!user || hasSyncedUser.current) return;
    hasSyncedUser.current = true;
    const firstName = localStorage.getItem("firstName");
    const lastName = localStorage.getItem("lastName");
    const email = localStorage.getItem("email");
    const phoneNumber = localStorage.getItem("phoneNumber");

    const userData = clean({ firstName, lastName, email, phoneNumber });
    if (Object.keys(userData).length) {
      updateUser(user.uid, null, userData);
      ["firstName", "lastName", "phoneNumber"].forEach(localStorage.removeItem);
    }
  }, [user, updateUser]);

  useEffect(() => {
    if (conversationId && user) {
      setSelectedConversation(conversationId);
    }
  }, [conversationId, user]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !isSending) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    if (isSending) return;
    setIsSending(true);

    try {
      const fileIds = await Promise.all(
        attachments.map(async (file) =>
          createFile(file, {
            name: file.name,
            type: "message",
            size: file.size,
            status: "uploading",
            mimeType: file.type,
          }),
        ),
      );
      await createMessageForConversation({
        content: input,
        conversationId: conversation!._id,
        fileIds,
      });
      setInput("");
      setAttachments([]);
      if (inputRef.current) inputRef.current.focus();
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    }

    setIsSending(false);
  };

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <LoadingView />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 dark:bg-zinc-900 dark:border-zinc-800">
        <ConversationHeaderView
          conversation={conversation}
          className="px-4 py-2"
        />
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        <div
          ref={chatContainerRef}
          className="flex flex-col flex-1 w-full px-4 py-2 space-y-2 overflow-y-auto"
        >
          {messages.length === 0 && (
            <EmptyStateView
              title="This is Casey, our AI assistant"
              message="Please say hello to introduce yourself and get started."
            />
          )}
          {messages.map((msg) => (
            <MessageListItem
              key={msg._id}
              conversation={conversation}
              message={msg}
            />
          ))}
        </div>

        <div className="p-4 bg-white border-t border-gray-200 dark:bg-zinc-900 dark:border-zinc-800">
          <ConversationDetailInput
            inputRef={inputRef}
            input={input}
            setInput={setInput}
            attachments={attachments}
            setAttachments={setAttachments}
            sendMessage={sendMessage}
            handleKeyDown={handleKeyDown}
          />
        </div>
      </div>
    </div>
  );
};

export default ConversationDetailPage;
